import {
  ICellRendererParams,
  ValueGetterParams
} from 'ag-grid-community';
import { dateFormatterCellRenderer } from '@grc/ui-utility';
import {
  FontAwesomeIcon,
  GrcTooltip,
  GrcTooltipPosition,
  IconProp,
  facheckclassicregular,
  facirlexmarkclassicregular,
  fapenlineclassicregular,
  ServerColumnOptions
} from '@grc/ui-package';
import { LocationPopOver } from '@grc/ui-shared';
import { IQuote } from '../../../models/quotes/quote.interface';
import { QuoteTabKey } from '../../../models/quotes/quote-tab.interface';
import { ILocationEntity } from '../../../common/location-popover/location-popover.interface';
import I18N from '../../../i18n/interface';
import {
  IQuoteGridRow,
  QuoteColDef
} from '../../../models/quote-management/quote-grid.interface';
import {
  IQuoteHandlingUnitType,
  IQuoteTransportationMode
} from '../useQuoteSettings';

interface IGetQuoteGridColumnsArgs {
  localeData: I18N;
  tab: QuoteTabKey;
  transportationModes: IQuoteTransportationMode[];
  handlingUnitTypes: IQuoteHandlingUnitType[];
  onEdit: (quoteId: string) => void;
  onApprove: (quoteId: string) => void;
  onReject: (quoteId: string) => void;
}

// Same shape/derivation as shipment-grid.columns.tsx's sumHandlingUnits -
// the display weight unit comes from each handling unit's own `weight.Unit`
// rather than a separate tenant UOM lookup (see quote.interface.ts's
// IQuoteHandlingUnit comment). Reads `handlingUnitRequests` (confirmed
// field name, not `handlingUnits`).
function sumHandlingUnits(quote?: IQuote | null) {
  const units = Object.values(quote?.handlingUnitRequests ?? {});
  const quantity = units.reduce(
    (total, unit) => total + (unit.quantity ?? 0),
    0
  );
  const weight = units.reduce(
    (total, unit) => total + (unit.weight?.Value ?? 0),
    0
  );
  const weightUnit = units[0]?.weight?.Unit?.split('.').pop() ?? '';
  return { units, quantity, weight, weightUnit, huCount: units.length };
}

// HuProduct column - joins each handling unit's `handlingUnitTemplateId`
// against GetQuoteSettings' HandlingUnitTypeDetails for a display name (e.g.
// "10 Pallet"), same modeId -> name join pattern as the Mode column.
function getHuProductDisplay(
  quote: IQuote | null | undefined,
  handlingUnitTypes: IQuoteHandlingUnitType[]
): string {
  const { units } = sumHandlingUnits(quote);
  if (!units.length) return '';
  return units
    .map(unit => {
      const typeName = handlingUnitTypes.find(
        type => type.id === unit.handlingUnitTemplateId
      )?.name;
      return `${unit.quantity} ${typeName ?? 'HU'}`;
    })
    .join(', ');
}

// What GET /v1/quotes can filter and sort on (grid serverPaging). Columns
// without it are computed or enriched, so the server cannot sort/filter them.
const server = (options: ServerColumnOptions) => ({ server: options });

function getCheckboxColumn(): QuoteColDef {
  return {
    colId: 'select',
    headerName: '',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    sortable: false,
    filter: false,
    floatingFilter: false,
    resizable: false,
    pinned: 'left',
    width: 44,
    maxWidth: 44
  };
}

// Actions content is the one thing that genuinely varies by tab: `Quote
// Requests` gets edit + reject, `Pending for Approval` gets accept + reject
// alongside its checkbox column, `Approved`/`Rejected`/`Cancelled` get no
// Actions column at all (Quote ID is the pinned column there instead).
function getActionsColumn(
  tab: QuoteTabKey,
  localeData: I18N,
  onEdit: (quoteId: string) => void,
  onApprove: (quoteId: string) => void,
  onReject: (quoteId: string) => void
): QuoteColDef | null {
  if (tab === 'quoteRequests') {
    return {
      headerName: localeData.Actions,
      colId: 'actions',
      pinned: 'left',
      sortable: false,
      filter: false,
      floatingFilter: false,
      minWidth: 100,
      cellRenderer: (params: ICellRendererParams<IQuote>) => {
        const quoteId = params.data?.id ?? '';
        return (
          <div className="quote-grid-actions">
            <GrcTooltip
              title={localeData.Edit}
              position={GrcTooltipPosition.Top}
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={localeData.Edit}
                className="quote-grid-action quote-grid-action-edit"
                onClick={() => onEdit(quoteId)}
              >
                <FontAwesomeIcon icon={fapenlineclassicregular as IconProp} />
              </span>
            </GrcTooltip>
            <GrcTooltip
              title={localeData.Reject}
              position={GrcTooltipPosition.Top}
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={localeData.Reject}
                className="quote-grid-action quote-grid-action-reject"
                onClick={() => onReject(quoteId)}
              >
                <FontAwesomeIcon
                  icon={facirlexmarkclassicregular as IconProp}
                />
              </span>
            </GrcTooltip>
          </div>
        );
      }
    };
  }

  if (tab === 'pendingForApproval') {
    return {
      headerName: localeData.Actions,
      colId: 'actions',
      pinned: 'left',
      sortable: false,
      filter: false,
      floatingFilter: false,
      minWidth: 100,
      cellRenderer: (params: ICellRendererParams<IQuote>) => {
        const quoteId = params.data?.id ?? '';
        return (
          <div className="quote-grid-actions">
            <GrcTooltip
              title={localeData.Accept}
              position={GrcTooltipPosition.Top}
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={localeData.Accept}
                className="quote-grid-action quote-grid-action-approve"
                onClick={() => onApprove(quoteId)}
              >
                <FontAwesomeIcon icon={facheckclassicregular as IconProp} />
              </span>
            </GrcTooltip>
            <GrcTooltip
              title={localeData.Reject}
              position={GrcTooltipPosition.Top}
            >
              <span
                role="button"
                tabIndex={0}
                aria-label={localeData.Reject}
                className="quote-grid-action quote-grid-action-reject"
                onClick={() => onReject(quoteId)}
              >
                <FontAwesomeIcon
                  icon={facirlexmarkclassicregular as IconProp}
                />
              </span>
            </GrcTooltip>
          </div>
        );
      }
    };
  }

  return null;
}

// Highlighted as a link per the mockup - same grc-text-link pattern
// shipment-grid.columns.tsx's Shipment ID column uses. Wired to the same
// onEdit callback the Quote Requests tab's pencil icon uses rather than a
// separate handler, since no dedicated quote-details route is confirmed yet
// (same "documented TBD" precedent, not a functional navigation change).
function getQuoteIdColumn(
  localeData: I18N,
  pinned: boolean,
  onEdit: (quoteId: string) => void
): QuoteColDef {
  return {
    headerName: localeData.QuoteId,
    field: 'quoteNumber',
    colId: 'quoteNumber',
    context: server({
      filterParam: 'quoteNumber',
      filterType: 'text',
      sortField: 'quoteNumber'
    }),
    pinned: pinned ? 'left' : undefined,
    minWidth: 140,
    cellRenderer: (params: ICellRendererParams<IQuote>) =>
      params.data ? (
        <span
          role="button"
          tabIndex={0}
          className="quote-grid-quote-id-link"
          onClick={() => onEdit(params.data?.id ?? '')}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onEdit(params.data?.id ?? '');
            }
          }}
        >
          {params.data.quoteNumber}
        </span>
      ) : null
  };
}

// Origin/Destination cells - resolved per page by useQuoteEntities onto the
// row's originLocation/destinationLocation and rendered with the shared
// @grc/ui-shared LocationPopOver rather than a local component. `labelNoContactToShow`/`defaultPhoneCountry`/
// `defaultContactId` are required by ILocationPopOverProps but unused inside
// the library's own render path when `showDefaultContact` is false (it falls
// back to the entity's own generalPhoneNumber/generalEmailAddress instead) -
// passed as empty strings rather than fabricating real values.
function renderLocationCell(
  entity: ILocationEntity | null | undefined,
  targetId: string
) {
  if (!entity?.name) {
    return <span className="quote-grid-location-empty">—</span>;
  }
  return (
    <LocationPopOver
      id={`${targetId}_popover`}
      targetId={targetId}
      location={entity}
      labelNoContactToShow=""
      defaultPhoneCountry=""
      defaultContactId=""
      showDefaultContact={false}
    />
  );
}

function getDataColumns(
  localeData: I18N,
  transportationModes: IQuoteTransportationMode[],
  handlingUnitTypes: IQuoteHandlingUnitType[]
): QuoteColDef[] {
  return [
    {
      headerName: localeData.Origin,
      field: 'originId',
      cellRenderer: (params: ICellRendererParams<IQuoteGridRow>) =>
        renderLocationCell(
          params.data?.originLocation,
          `quote_origin_${params.data?.id}`
        ),
      minWidth: 200
    },
    {
      headerName: localeData.Destination,
      field: 'destinationId',
      cellRenderer: (params: ICellRendererParams<IQuoteGridRow>) =>
        renderLocationCell(
          params.data?.destinationLocation,
          `quote_destination_${params.data?.id}`
        ),
      minWidth: 200
    },
    {
      headerName: localeData.Mode,
      field: 'transportationModeId',
      valueGetter: (params: ValueGetterParams<IQuote>) =>
        transportationModes.find(
          mode => mode.id === params.data?.transportationModeId
        )?.displayName ?? '',
      minWidth: 130
    },
    {
      headerName: localeData.PUDateAndTime,
      field: 'pickup',
      context: server({
        filterParam: 'pickup',
        filterType: 'dateRange',
        sortField: 'pickupDate'
      }),
      cellRenderer: (params: ICellRendererParams<IQuote>) =>
        dateFormatterCellRenderer(params.data?.pickup?.readyDate),
      minWidth: 170
    },
    {
      headerName: localeData.DeliveryDateAndTime,
      field: 'delivery',
      context: server({
        filterParam: 'delivery',
        filterType: 'dateRange',
        sortField: 'deliveryDate'
      }),
      cellRenderer: (params: ICellRendererParams<IQuote>) =>
        dateFormatterCellRenderer(params.data?.delivery?.readyDate),
      minWidth: 170
    },
    {
      headerName: localeData.HuProduct,
      field: 'handlingUnitRequests',
      valueGetter: (params: ValueGetterParams<IQuote>) =>
        getHuProductDisplay(params.data, handlingUnitTypes),
      minWidth: 160
    },
    {
      headerName: localeData.TotalQuantity,
      colId: 'totalQuantity',
      valueGetter: (params: ValueGetterParams<IQuote>) =>
        sumHandlingUnits(params.data).quantity,
      cellStyle: { textAlign: 'right' },
      minWidth: 140
    },
    {
      headerName: localeData.TotalWeight,
      colId: 'totalWeight',
      valueGetter: (params: ValueGetterParams<IQuote>) => {
        const { weight, weightUnit } = sumHandlingUnits(params.data);
        return weightUnit ? `${weight} ${weightUnit}` : weight;
      },
      minWidth: 140
    },
    {
      headerName: localeData.TotalCharges,
      colId: 'totalCharges',
      valueGetter: (params: ValueGetterParams<IQuote>) => {
        const cost = params.data?.cost;
        if (cost?.totalCost === undefined) return '';
        return cost.currency
          ? `${cost.totalCost} ${cost.currency}`
          : cost.totalCost;
      },
      minWidth: 140
    }
  ];
}

// Full column set per tab (per direction given after the plan's original
// "mockup-visible-columns-only" scoping): `Quote Requests` and
// `Pending for Approval` lead with a pinned Actions column (+ a pinned
// checkbox column for `Pending for Approval`); `Approved`/`Rejected`/
// `Cancelled` have no Actions column and pin Quote ID instead; `Audit` isn't
// built from this factory at all (shell-only, plan §5/§9 item 3).
export function getQuoteGridColumns({
  localeData,
  tab,
  transportationModes,
  handlingUnitTypes,
  onEdit,
  onApprove,
  onReject
}: IGetQuoteGridColumnsArgs): QuoteColDef[] {
  const columns: QuoteColDef[] = [];
  const hasActions = tab === 'quoteRequests' || tab === 'pendingForApproval';

  if (tab === 'pendingForApproval') {
    columns.push(getCheckboxColumn());
  }

  const actionsColumn = getActionsColumn(
    tab,
    localeData,
    onEdit,
    onApprove,
    onReject
  );
  if (actionsColumn) {
    columns.push(actionsColumn);
  }

  columns.push(getQuoteIdColumn(localeData, !hasActions, onEdit));

  columns.push(
    ...getDataColumns(
      localeData,
      transportationModes,
      handlingUnitTypes
    )
  );

  return columns;
}

export default getQuoteGridColumns;
